import { WeaponLevel } from '@components/arsenal/weapons/WeaponLevel';
import { HoverMenu } from '@components/helpers/HoverMenu';
import { capitalize } from '@helpers/dotNotation';
import { useActivateWeaponMutation } from '@http';
import { useTranslation } from 'react-i18next';
import { IconLock } from '@tabler/icons-react';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

interface WeaponButtonProps {
  weapon: Shared.WeaponData
  index: number;
  onView: () => void;
  onUpgrade: () => void;
};

export function WeaponButton({ weapon, index, onView, onUpgrade }: WeaponButtonProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Arsenal.Weapons' });
  const { userData, refetch, setIsFetching } = useAppStore();
  const { mutateAsync } = useActivateWeaponMutation();

  const getClasses = () => {
    const base = index % 2 === 0 ? 'border-r-1' : '';
    const added = index % 3 !== 2 ? 'lg:border-r-1' : '';
    return `weapon-button border-b-1 border-0 bg-transparent ${base} ${added}`;
  };

  const isActive = userData.weapons.some(c => c.key === weapon.key && c.active);
  const isLocked = !userData.weapons.some(c => c.key === weapon.key);
  const userWeapon = userData.weapons.find(c => c.key === weapon.key);
  if (!userWeapon) return null;

  const equip = async () => {
    setIsFetching(true);
    await mutateAsync({ id: userWeapon.id });
    refetch();
  };

  const hoverItems = [
    {
      label: t('equip'),
      className: 'btn-active',
      visible: !isActive,
      onClick: equip
    },
    {
      label: t('upgrade'),
      className: isActive ? 'btn-active' : '',
      onClick: onUpgrade
    },
    {
      label: t('view'),
      onClick: onView
    },
  ];

  return (
    <HoverMenu className={getClasses()} items={hoverItems}>
      <div className="bg-1" />
      <img
        src={`/img/weapons/${weapon.key}.avif`}
        className="weapon-img blur-animation"
        alt={weapon.key}
      />
      <div className="bg-2" />

      {isLocked && <div className="bg-1 grid-overlay" />}

      <div className="text-container">
        <div className="w-full">
          <div className="flex items-center justify-center pb-1">
            {isLocked && <IconLock className="text-red-500 mr-1" size={16} />}
            <p className="weapon-name">{capitalize(weapon.key)}</p>
          </div>
          {!isLocked && <WeaponLevel xp={userWeapon.xp} />}
        </div>
        {isActive && <p className="weapon-active">{t('active')}</p>}
      </div>
    </HoverMenu>
  );
}